
import { defineComponent, ref, onMounted, computed } from "vue";
import router from "@/router";
import store from "@/store";
import NativeClient, { TitleInfo } from "@/client/native-client";
import {
  PantryFridgeImageData,
  RequstCameraImage,
  CameraImageMap,
  FRIDGE_INFO_ROOM_ID_TYPE,
  FRIDGE_AREA_ID_TYPE,
  PantryOpticalAxis
} from "@/types/pantry";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";
import PantryOpticalAxisCompleteDialog from "@/components/pantry/PantryOpticalAxisCompleteDialog.vue";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
export default defineComponent({
  name: "PantryOptionAxisPage",
  components: {
    CommonDesignDialog,
    PantryOpticalAxisCompleteDialog
  },
  setup() {
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
    } = useApiErrorHandler();
    const ERROR_BUTTON_LABEL = "OK";
    const { closeDialog } = useApiErrorDialog();
    const getApplianceId = () => {
      let applianceId = String(router.currentRoute.value.query.applianceId);
      let result = "";
      if (applianceId == 'undefined') {
        NativeClient.sendDebugLog("getApplianceId empty");
        applianceId = ""
      } else {
        result = decodeURIComponent(applianceId);
      }
      return result;
    };
    const applianceId = getApplianceId();
    const image = ref<HTMLImageElement>();
    const rotationAngle = ref(0);
    const opticalAxis = ref(0);
    const isShowSaveCompleteDialog = ref(false);
    const isShowNoImageDialog = ref(false);
    NativeClient.setTitle(new TitleInfo(false, ""));
    NativeClient.sendDebugLog(
      "PantryOptionAxisPage applianceId=" +
        applianceId
    );
    const showNoImageDialog = () => {
      NativeClient.showCameraCloseButton(false);
      isShowNoImageDialog.value = true;
    }

    const setImage = (base64Data: string, width: number, height: number, imageWidth: number, imageHeight: number) => {
      NativeClient.sendDebugLog(
        "PantryOptionAxisPage setImage"
      );
      if (image.value == undefined) {
        return;
      }
      image.value.src = "data:image/jpeg;base64," + base64Data;
      image.value.decoding = "async";
      image.value.onload = function() {
        if (image.value == undefined) {
          return;
        }
        NativeClient.sendDebugLog(
          "PantryOptionAxisPage drawImageData loaded width=" +
            width +
            " height=" +
            height
        );
        NativeClient.sendDebugLog(
          "PantryOptionAxisPage drawImageData loaded image.width=" +
            image.value.width +
            " image.height=" +
            image.value.height
        );
        const viewWidth = width;
        const viewHeight = height;
        let scaleRate = viewWidth / imageWidth;
        let scaleWidth = viewWidth;
        let scaleHeight = imageHeight * scaleRate;
        if (scaleHeight > viewHeight) {
          scaleRate = viewHeight / imageHeight;
          scaleWidth = imageWidth * scaleRate;
          scaleHeight = viewHeight;
        }
        NativeClient.sendDebugLog(
          "PantryOptionAxisPage drawImageData loaded scaleWidth=" +
            scaleWidth +
            " scaleHeight=" +
            scaleHeight
        );
        image.value.style.width = scaleWidth + "px";
        image.value.style.height = scaleHeight + "px";
        NativeClient.sendDebugLog("PantryOptionAxisPage drawImageData window.innerWidth=" + window.innerWidth + " window.innerHeight=" + window.innerHeight);
        NativeClient.sendDebugLog("PantryOptionAxisPage drawImageData scaleWidth=" + scaleWidth + " window.innerHeight=" + scaleHeight);
      }
    };

    const getFridgeImage = (data: PantryFridgeImageData) => {
      NativeClient.sendDebugLog("PantryOptionAxisPage getFridgeImage data=" + JSON.stringify(data));
      const map: CameraImageMap = store.getters["pantry/getCameraImageMap"];
      if (map && data.key) {
        NativeClient.sendDebugLog(
          "PantryOptionAxisPage getCameraImageMap success"
        );
        let imageData = null;
        const item = map.map.get(data.key);
        NativeClient.sendDebugLog(
          "PantryOptionAxisPage getFridgeImage key=" + data.key
        );
        if (item && item.imageData) {
          imageData = item.imageData;
        } else {
          NativeClient.sendDebugLog(
            "PantryOptionAxisPage getFridgeImage no data item=" +
              JSON.stringify(item)
          );
        }
        if (data.info && imageData) {
          const width = window.innerWidth;
          const height = window.innerHeight;
          setImage(imageData, width, height, data.info.horizontal, data.info.vertical);
        }
      } else {
        NativeClient.sendDebugLog(
          "PantryOptionAxisPage getFridgeImage failed key=" + data.key
        );
      }
      store.dispatch("common/stopLoading");
      NativeClient.sendDebugLog("PantryOptionAxisPage getFridgeImage end");
    };

    const requestImageLoad = async (applianceId : string, data: PantryFridgeImageData) => {
      if (data.info) {
        const payload = new RequstCameraImage(data.info.imageUrl, data.imageUrlFilePath, applianceId);
        await Promise.all([store.dispatch("pantry/getCameraImageLoad", payload)]).then(result => {
          NativeClient.sendDebugLog("PantryOptionAxisPage requestImageLoad result");
          getFridgeImage(data);
        }).catch(error => {
          NativeClient.sendDebugLog("PantryOptionAxisPage requestImageLoad error " + JSON.stringify(error));
          NativeClient.showCameraCloseButton(false);
        });
      } else {
        store.dispatch("common/stopLoading");
      }
    };

    const requestFridgeList = async (applianceId: string) => {
      await Promise.all([
        store.dispatch("pantry/getFridgeListInfo", applianceId)
      ]).then(result => {
        const imageInfoList = store.getters["pantry/getPantryFridgeListInfo"];
        let target = null;
        // 冷蔵室/正面棚を対象
        for (const roomInfo of imageInfoList.rooms) {
          if (roomInfo.roomId != FRIDGE_INFO_ROOM_ID_TYPE.FRIDGE_INFO_FRIDGE) {
            continue;
          }
          for (const imageInfo of roomInfo.images) {
            if (imageInfo.areaId != FRIDGE_AREA_ID_TYPE.FRONT_SHELF) {
              continue;
            }
            target = new PantryFridgeImageData(0, roomInfo.roomId, imageInfo, applianceId);;
            break;
          }
          if (target != null) {
            break;
          }
        }
        if (target != null) {
          NativeClient.sendDebugLog("[Angle]PantryOptionAxisPage requestFridgeList result target.info=" + JSON.stringify(target.info));
          if (target.info) {
            opticalAxis.value = target.info.opticalAxis;
          }
          NativeClient.sendDebugLog("[Angle]PantryOptionAxisPage requestFridgeList result opticalAxis=" + opticalAxis.value);
          requestImageLoad(applianceId, target);
        } else {
          store.dispatch("common/stopLoading");
          showNoImageDialog();
        }
      }).catch(error => {
          NativeClient.sendDebugLog("PantryOptionAxisPage requestFridgeList error " + JSON.stringify(error));
          NativeClient.showCameraCloseButton(false);
      });
    };

    const requestGetPantry = async () => {
      store.dispatch("common/startLoading");
      await Promise.all([NativeClient.getApiParams()]).then(result => {
        const apiBaseParams = store.getters["common/getApiBaseParams"];
        requestFridgeList(applianceId);
      }).catch(error => {
          NativeClient.sendDebugLog("PantryOptionAxisPage requestGetPantry error " + JSON.stringify(error));
          NativeClient.showCameraCloseButton(false);
      });
    };

    const setRotate = (angle: number) => {
      if (image.value == undefined) {
        return;
      }
      NativeClient.sendDebugLog("[Angle]PantryOptionAxisPage setRotate angle=" + angle);
      image.value.style.transform = "rotate(" + angle + "deg)";
      rotationAngle.value = angle;
    };

    const clickLeft = () => {
      NativeClient.sendDebugLog("[Angle]PantryOptionAxisPage clickLeft rotationAngle=" + rotationAngle.value + " opticalAxis=" + opticalAxis.value);
      const check = rotationAngle.value - 0.5 + opticalAxis.value;
      NativeClient.sendDebugLog("[Angle]PantryOptionAxisPage clickLeft angle=" + check);
      if (check < -10) {
        return;
      }
      const angle = rotationAngle.value - 0.5;
      setRotate(angle);
    };

    const clickRight = () => {
      NativeClient.sendDebugLog("[Angle]PantryOptionAxisPage clickRight rotationAngle=" + rotationAngle.value + " opticalAxis=" + opticalAxis.value);
      const check = rotationAngle.value + 0.5 + opticalAxis.value;
      NativeClient.sendDebugLog("[Angle]PantryOptionAxisPage clickRight angle=" + check);
      if (check > 10) {
        return;
      }
      const angle = rotationAngle.value + 0.5;
      setRotate(angle);
    };

    const showShowSaveCompleteDialog = () => {
      NativeClient.showCameraCloseButton(false);
      isShowSaveCompleteDialog.value = true;
    };

    const clickSave = async () => {
      store.dispatch("common/startLoading");
      await Promise.all([NativeClient.getApiParams()]).then(result => {
        NativeClient.sendDebugLog("[Angle]PantryOptionAxisPage clickSave result 1");
        const payload = new PantryOpticalAxis(applianceId, rotationAngle.value + opticalAxis.value);
        Promise.all([store.dispatch("pantry/setOpticalAxis", payload)]).then(result => {
          store.dispatch("common/stopLoading");
          NativeClient.sendDebugLog("[Angle]PantryOptionAxisPage clickSave result 2");
          showShowSaveCompleteDialog();
        }).catch(error => {
          NativeClient.sendDebugLog("PantryOptionAxisPage clickSave error " + JSON.stringify(error));
          NativeClient.showCameraCloseButton(false);
        });
      }).catch(error => {
        NativeClient.sendDebugLog("PantryOptionAxisPage clickSave error " + JSON.stringify(error));
        NativeClient.showCameraCloseButton(false);
      });
    };

    const clickCancel = () => {
      NativeClient.pageClose();
    };

    const clickSaveComplete = () => {
      isShowSaveCompleteDialog.value = false;
      NativeClient.showCameraCloseButton(true);
      NativeClient.pageClose();
    };

    /*
     * 庫内画像を取得する
     */
    const execute = async () => {
      requestGetPantry();
    };

    const closeError = () => {
      closeDialog();
      NativeClient.showCameraCloseButton(true);
    };

    const styleLeft = computed(() => {
      let textColor = "#000000";
      const angle = rotationAngle.value + opticalAxis.value;
      NativeClient.sendDebugLog("[Angle]PantryOptionAxisPage styleLeft angle=" + angle);
      if (angle <= -10) {
        textColor = "rgba(155, 155, 155, 0.9)";
      }
      return { color: textColor };
    });

    const styleRight = computed(() => {
      let textColor = "#000000";
      const angle = rotationAngle.value + opticalAxis.value;
      NativeClient.sendDebugLog("[Angle]PantryOptionAxisPage styleRight angle=" + angle);
      if (angle >= 10) {
        textColor = "rgba(155, 155, 155, 0.9)";
      }
      return { color: textColor };
    });

    const closeNoImageDialog = () => {
      isShowNoImageDialog.value = false;
      NativeClient.showCameraCloseButton(true);
      NativeClient.pageClose();
    }
    /**
     * コンポーネントがマウントされたときに通知される
     */
    onMounted(() => {
      NativeClient.sendDebugLog("PantryOptionAxisPage onMounted");
      execute();
    });
    return {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      image,
      clickLeft,
      clickRight,
      clickSave,
      clickCancel,
      isShowSaveCompleteDialog,
      clickSaveComplete,
      closeDialog,
      styleLeft,
      styleRight,
      closeError,
      ERROR_BUTTON_LABEL,
      isShowNoImageDialog,
      closeNoImageDialog
    };
  }
});
